import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@kidshome/ui';
import { useAuthStore, authApi } from '@/entities/session';

/**
 * The persistent, unmissable "acting as platform owner" banner (ADR-0055). Shown on EVERY screen while
 * a platform owner is impersonating a household (`actingAs` set in the auth store, which also makes the
 * api-client echo `X-Act-As-Household`). It names the target household (RAW user content, never
 * translated — ADR-0025) and offers a single Exit.
 *
 * Exit stops the impersonation by construction: it clears `actingAs` (so the header is no longer sent),
 * clears the TanStack cache (so every query refetches as the OWNER, never serving target-household data
 * cached under the owner's session), and returns to the unified `/platform` console. Quiet when not impersonating →
 * renders nothing (no layout shift). Uses a warning/attention tone + semantic tokens + logical
 * properties so it reads correctly under either theme and in RTL.
 */
export function ActingAsBanner(): React.ReactElement | null {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const actingAs = useAuthStore((s) => s.actingAs);
  const setActingAs = useAuthStore((s) => s.setActingAs);
  const setUser = useAuthStore((s) => s.setUser);

  if (actingAs === null) return null;

  const onExit = async (): Promise<void> => {
    // Stop sending the header AND drop every cached query in the SAME synchronous tick (React 18+
    // batches both into one commit) so the banner can never disappear while a target-household query
    // is still rendering its cached result unlabeled — the two effects (hide the warning, evict the
    // data it was warning about) land together. THEN refetch `me` (now resolved as the operator's own
    // principal, since the header is already gone). Both a separate super-admin (ADR-0072) and an
    // env-allowlisted owner return to the unified platform console (ADR-0077); the guard re-resolves
    // the principal on navigation.
    setActingAs(null);
    queryClient.clear();
    try {
      setUser(await authApi.fetchMe());
    } catch {
      // Non-fatal — the guard re-resolves `me` on navigation.
    }
    void navigate('/platform', { replace: true });
  };

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warning/50 bg-warning-fg px-4 py-2 text-sm lg:px-6"
    >
      <span className="flex items-center gap-1.5 font-semibold text-warning">
        <span aria-hidden="true">{'⚠'}</span>
        {t('platform.actingAs.banner', { household: actingAs.name })}
      </span>
      <Button variant="secondary" size="sm" onClick={() => void onExit()} className="ms-auto">
        {t('platform.actingAs.exit')}
      </Button>
    </div>
  );
}
